import {
  NodeMessages,
  UserSettingsCard,
  UserSettingsFlowType,
  Button,
} from "@ory/elements";
import { Authentication } from "@pages/authentication/Authentication";
import { useEffect, useState } from "react";
import {
  SettingsFlow,
  UiNode,
  UiText,
  UpdateSettingsFlowBody,
} from "@ory/client";

import { Link, useNavigate, useSearchParams } from "react-router";
import { ErrorData, handleError } from "@pages/authentication/error";
import { routes } from "@routes";
import { ory } from "@services/authentication";
import { Icon, Spinner } from "@thetreep/ui";
import { useTranslation } from "react-i18next";
import { translateFlow } from "./translations";
import { useUserStore } from "@stores/userStore";
import { useBookingStore } from "@stores/useBookingStore";
import { ConfirmDeleteAccountModal } from "./ConfirmDeleteAccountModal";
import theme from "@theme";
import { useToaster } from "@hooks/useToaster";
import { forgetMe } from "@services/user";
import { Role } from "@gen/user/v1/user_pb";
import styled from "styled-components";

const StyledUserSettingsCard = styled(UserSettingsCard)`
  [data-testid="node/text/password-rules/text"] {
    font-size: 12px;
    color: ${theme.colors.gray[500]};
    margin-top: -12px;
  }
`;

export function Settings() {
  const [flow, setFlow] = useState<SettingsFlow | null>(null);
  const [uiMessages, setUiMessages] = useState<UiText[]>([]);
  const [isConfirmDeleteAccountModalOpen, setIsConfirmDeleteAccountModalOpen] =
    useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { fetchUser, clearUser, user } = useUserStore();
  const { resetBookingForm } = useBookingStore();
  const { addToast } = useToaster();
  const returnTo = window.location.origin;
  const { t } = useTranslation();

  const onLogout = async () => {
    try {
      const { data: flow } = await ory().createBrowserLogoutFlow();
      await ory().updateLogoutFlow({
        token: flow.logout_token,
      });
      clearUser();
      resetBookingForm();
      navigate(routes.LOGIN);
    } catch (error) {
      console.error("error logging out", error);
    }
  };

  const enhancePasswordFlowWithRules = (flow: SettingsFlow): SettingsFlow => {
    if (!flow?.ui?.nodes) {
      return flow;
    }

    const passwordNodes = flow.ui.nodes.filter(
      (node) => node.group === "password",
    );

    if (passwordNodes.length === 0) {
      return flow;
    }

    const rulesNode: UiNode = {
      type: "text",
      group: "password",
      attributes: {
        node_type: "text" as const,
        id: "password-rules",
        text: {
          id: 4000001, // used for the translation
          text: "", // Will be provided by the translation
          type: "info",
        },
      },
      meta: {},
      messages: [],
    };

    const passwordInputIndex = flow.ui.nodes.findIndex(
      (node) =>
        node.group === "password" &&
        node.attributes.node_type === "input" &&
        (node.attributes as { name?: string }).name === "password",
    );

    if (passwordInputIndex !== -1) {
      const enhancedNodes = [
        ...flow.ui.nodes.slice(0, passwordInputIndex + 1),
        rulesNode,
        ...flow.ui.nodes.slice(passwordInputIndex + 1),
      ];

      return {
        ...flow,
        ui: {
          ...flow.ui,
          nodes: enhancedNodes,
        },
      };
    }

    return flow;
  };

  const extractUiMessagesFromFlow = (flow: SettingsFlow) => {
    const uiMessages: UiText[] = [];

    if (flow?.ui?.messages && flow.ui.messages.length > 0) {
      uiMessages.push(...flow.ui.messages);
    } else if (flow?.ui?.nodes) {
      const nodeErrors = flow.ui.nodes
        .filter((node) => node.messages && node.messages.length > 0)
        .flatMap((node) => node.messages || []);

      if (nodeErrors.length > 0) {
        uiMessages.push(...nodeErrors);
      }
    }

    return uiMessages;
  };

  const setFlowState = (flow: SettingsFlow) => {
    const enhancedFlow = enhancePasswordFlowWithRules(flow);
    setFlow(enhancedFlow);
  };

  const setFlowAndUiMessages = (flow: SettingsFlow) => {
    const uiMessages = extractUiMessagesFromFlow(flow);
    setFlowState(flow);
    setUiMessages(uiMessages);
  };

  const errorHandler: (error: ErrorData) => unknown = handleError(
    (flowId: string) => getFlow(flowId),
    setFlowAndUiMessages,
    routes.LOGIN,
    true,
    navigate,
  );

  const getFlow = async (flowId: string) => {
    try {
      const res = await ory().getSettingsFlow({ id: flowId });
      setFlowAndUiMessages(translateFlow(t, res.data));
    } catch (e) {
      errorHandler(e as ErrorData);
    }
  };

  const createFlow = async () => {
    try {
      const res = await ory().createBrowserSettingsFlow({
        ...(returnTo && { returnTo }),
      });

      setFlowAndUiMessages(translateFlow(t, res.data));
      setSearchParams({ flow: res.data.id });
    } catch (error) {
      errorHandler(error as ErrorData);
    }
  };

  const submitFlow = async (body: UpdateSettingsFlowBody) => {
    if (!flow) {
      navigate(routes.REGISTRATION, { replace: true });
      return;
    }

    try {
      const res = await ory().updateSettingsFlow({
        flow: flow.id,
        updateSettingsFlowBody: body,
      });
      setFlowAndUiMessages(translateFlow(t, res.data));
      fetchUser();
    } catch (error) {
      const errorData = error as ErrorData;
      if (
        errorData.response?.status === 401 ||
        errorData.response?.status === 403
      ) {
        onLogout();
        return;
      } else if (errorData.response?.status === 400) {
        const errorFlow = errorData.response?.data as SettingsFlow;
        const uiMessages = extractUiMessagesFromFlow(errorFlow);

        await createFlow(); // Reset the form, but store the error messages

        setUiMessages(uiMessages);
        return;
      }

      errorHandler(errorData);
    }
  };

  useEffect(() => {
    const flowId = searchParams.get("flow");
    const currentFlowId = flow?.id;
    if (flowId) {
      if (flowId !== currentFlowId) {
        getFlow(flowId);
      }
      return;
    }
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    createFlow();
  }, [searchParams]);

  useEffect(() => {
    if (flow?.ui?.messages && flow.ui.messages.length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [flow?.ui?.messages]);

  useEffect(() => {
    if (uiMessages.length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [uiMessages]);

  if (!flow) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="w-full pt-12 md:pt-20 pb-6 flex flex-col gap-6 px-5 mx-auto ">
      <div className="font-jakarta text-[28px] md:text-[32px] font-bold">
        {t("profileMenu.account")}
      </div>

      <Authentication type="settings">
        <div className="settingsContainer">
          <div className="settingCard">
            <NodeMessages uiMessages={uiMessages} />

            {(["profile", "password"] as UserSettingsFlowType[]).map(
              (flowType: UserSettingsFlowType) => {
                return (
                  <StyledUserSettingsCard
                    key={`${flowType}-${flow.id}`}
                    className="authCard"
                    flow={flow}
                    method={flowType}
                    includeScripts={true}
                    onSubmit={({ body }) =>
                      submitFlow(body as UpdateSettingsFlowBody)
                    }
                  />
                );
              },
            )}
          </div>
          <div className={`gdpr-card`}>
            <h3>{t("settings.gdpr.title")}</h3>
            <div className="w-full">
              <div className="max-w-fit flex flex-col gap-6">
                <div className="flex items-center text-cnam-blue font-bold">
                  <Icon
                    className="mr-2"
                    src="arrow_right_line"
                    color={theme.colors.cnam.blue.DEFAULT}
                  />
                  <span className="block mr-1 ">
                    {t("settings.gdpr.cguAccessText")}
                  </span>
                  <Link
                    to={{ pathname: routes.TERMS_AND_CONDITIONS }}
                    className="underline"
                  >
                    {t("settings.gdpr.cguAccessLink")}
                  </Link>
                </div>
                {user?.role !== Role.ADMIN && (
                  <Button
                    header={t("settings.gdpr.forgetMe")}
                    className="!ml-0"
                    onClick={() => {
                      setIsConfirmDeleteAccountModalOpen(true);
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </Authentication>

      <ConfirmDeleteAccountModal
        isOpen={isConfirmDeleteAccountModalOpen}
        onClose={() => setIsConfirmDeleteAccountModalOpen(false)}
        isLoading={isDeletingAccount}
        onConfirm={async () => {
          setIsDeletingAccount(true);
          try {
            await forgetMe();
            onLogout();
            addToast("success", t("settings.gdpr.deleteAccountSuccess"));
          } catch (e) {
            console.error(e);
          } finally {
            setIsDeletingAccount(false);
          }
        }}
      />
    </div>
  );
}
